// MapToolsMenu — vertikales Button-Menü als Google-Karten-Control (Chef-Direktive 2026-06-20: Karten-UI in die Karte).
//
// Generischer Button-Stack (default LEFT_BOTTOM = Eck links unten, vertikal). Die View fügt Buttons hinzu
// (addButton) und verdrahtet sie an bestehende Aktionen/Events — das Modul macht NUR die UI + Toggle-Zustand.
// google wird injiziert (für google.maps.ControlPosition).
import { resolveControlPosition } from "../adapter/providerConstants.js";
// topo2 Button-Basis (Tokens + .topo2-btn/--icon/--outline + SVG-Sizing). Co-located: reist mit dem Consumer,
// egal über welchen Vite-Entry er gebündelt wird. Vite dedupliziert den Import über Module hinweg.

// „weitere Topotheken" — Marker-Logo (Topothek-Pin). Pin nutzt --topo2-accent (global auf :root, theme-bar) statt
// --main-topo-color (das nur im Marker-Scope #map_canvas .svgMarker definiert ist → im Karten-Control nicht vererbt).
// Das weiße Badge-Rechteck bleibt: Logo-Identität + Kontrast für den Pin bei Hover/Active (solid-BG).
export const ICON_FURTHER_ARCHIVES =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 41.8 43" aria-hidden="true">'
    + '<rect width="41.8" height="43" rx="5.7" ry="5.7" style="fill:#fff"></rect>'
    + '<path d="M24.8,2.4h10c2.3,0,4.3,1.9,4.3,4.3v.3l-15.7,11.3c-.3-.3-.7-.7-1.1-.9-.4-.3-.8-.5-1.3-.7,0,0,3.8-14.3,3.8-14.3ZM39.1,36s0,4.3-4.4,4.3H7.2c-2.3,0-4.3-1.9-4.3-4.3V6.6c0-2.3,1.9-4.3,4.3-4.3h13.2l-3.6,13.8c-2.2.3-4.2,1.5-5.5,3.4-2.4,3.6-1.5,8.5,2.1,11,3.6,2.4,8.5,1.5,11-2.1,1.3-2,1.6-4.3,1.1-6.4l13.6-9.8v23.8" style="fill:var(--topo2-accent)"></path>'
    + '<path d="M14.6,21.7c1.2-1.8,3.6-2.2,5.4-1s2.2,3.6,1,5.4-3.6,2.2-5.4,1c-1.8-1.2-2.2-3.6-1-5.4h0Z" style="fill:var(--topo2-accent)"></path>'
    + '</svg>';

// „zentrieren" — Chef-SVG 2026-06-22: gefüllte Eck-Klammern + Mittelpunkt. currentColor → folgt der topo2-Button-Farbe.
// (Illustrator-Cruft entfernt: defs/clipPath/style-Klassen; die clip-path war die volle viewBox = wirkungslos → weg.
//  fill am <svg>-Root, die Formen erben → eine Farbe, dynamisch.)
export const ICON_FIT_TO_VIEW =
    '<svg viewBox="0 0 24.8 24.8" aria-hidden="true" fill="currentColor">'
    + '<path d="M12.4,15.94c1.96,0,3.54-1.59,3.54-3.54s-1.59-3.54-3.54-3.54-3.54,1.59-3.54,3.54,1.59,3.54,3.54,3.54"/>'
    + '<polygon points="0 0 0 9.55 4.28 9.55 4.28 4.31 9.55 4.31 9.55 0 0 0"/>'
    + '<polygon points="0 15.25 0 24.8 9.55 24.8 9.55 20.52 4.31 20.52 4.31 15.25 0 15.25"/>'
    + '<polygon points="15.25 0 15.25 4.31 20.52 4.31 20.52 9.55 24.8 9.55 24.8 0 15.25 0"/>'
    + '<polygon points="20.49 15.25 20.49 20.52 15.25 20.52 15.25 24.8 24.8 24.8 24.8 15.25 20.49 15.25"/>'
    + '</svg>';

// „Suchkreis" — Chef-SVG 2026-06-22: Lupe (Linse + Griff) mit horizontalem Doppelpfeil in der Linse. currentColor
// (Illustrator-Cruft entfernt: defs/clipPath/style-Klassen → einfarbiger Stroke-Pfad).
export const ICON_SEARCH_CIRCLE =
    '<svg viewBox="0 0 59.8 59.8" aria-hidden="true">'
    + '<g fill="none" stroke="currentColor" stroke-width="4.3" stroke-linecap="round" stroke-linejoin="round">'
    + '<circle cx="24.9" cy="24.9" r="22.8"/>'
    + '<line x1="42.3" y1="42.3" x2="58.3" y2="58.3"/>'
    + '<line x1="12.7" y1="25.1" x2="37.1" y2="25.1"/>'
    + '<polyline points="18.5 16.2 9.8 25 18.5 33.9"/>'
    + '<polyline points="31 16.2 39.7 25 31 33.9"/></g></svg>';

// „Suchkreis löschen" — Chef-SVG 2026-06-22: Lupe mit X in der Linse. currentColor (Kreuz einfarbig — die im
// Illustrator-Export „blauen" st2-Pfade waren leere Reste; das X selbst ist rosa → currentColor).
export const ICON_CLEAR_CIRCLE =
    '<svg viewBox="0 0 59.8 59.8" aria-hidden="true">'
    + '<g fill="none" stroke="currentColor" stroke-width="4.3" stroke-linecap="round" stroke-linejoin="round">'
    + '<circle cx="24.9" cy="24.9" r="22.8"/>'
    + '<line x1="42.3" y1="42.3" x2="58.3" y2="58.3"/>'
    + '<line x1="13.9" y1="35.9" x2="36" y2="13.9"/>'
    + '<line x1="36" y1="35.9" x2="13.9" y2="13.9"/></g></svg>';

// OV-Panel-Auslöser — Platzhalter „OV" als SVG-Text (currentColor). BEWUSST als SVG (nicht Textknoten): so kann der
// Chef es später 1:1 durch ein echtes Overlay-Icon ersetzen, ohne den Button-Typ zu ändern (bleibt icon-outline wie die anderen).
export const ICON_OVERLAYS =
    '<svg viewBox="0 0 24 24" aria-hidden="true">'
    + '<text x="12" y="13" text-anchor="middle" dominant-baseline="middle" '
    + 'font-family="inherit" font-size="10" font-weight="700" fill="currentColor">OV</text></svg>';

export function createMapToolsMenuClass(google) {

    return class MapToolsMenu {
        constructor(map, opts = {}) {
            this._map = map;
            this._pos = resolveControlPosition(google, opts.position || 'LEFT_BOTTOM');
            this._pairs = new Map();   // pair-id → Pair-Container (Chef 5: zwei Buttons in einem Slot)
            this._container = this._buildContainer();
            this.attach();
        }

        // (Re-)hängt das Menü in die Karten-Controls — IDEMPOTENT: pusht nur, wenn der Container nicht schon in der
        // Controls-MVCArray steckt. So überlebt es Refresh/Tiles-Reload (die die Controls leeren), OHNE ein zweites
        // Menü anzulegen. Bewusst über die MVCArray (nicht DOM): Google platziert Control-Elemente asynchron.
        attach() {
            if (this._pos === undefined) { return; }
            const arr = this._map.controls[this._pos];
            if (!arr) { return; }
            for (let i = 0; i < arr.getLength(); i++) {
                if (arr.getAt(i) === this._container) { return; }   // schon drin → nicht doppelt pushen
            }
            arr.push(this._container);
        }

        _buildContainer() {
            const c = document.createElement('div');
            // Gemeinsame Stack-Klasse (flex-column + gap + margin) — dieselbe wie der Zoom-Stack rechts.
            c.className = 'topo-map-tools topo2-btn-stack';
            return c;
        }

        // { key, svg, title, toggle?, active?, onClick(), onToggle(active) }
        addButton(cfg = {}) {
            const b = document.createElement('button');
            b.type = 'button';
            // topo2 Basis: Icon-Button, Outline-Variante. Toggle-Zustand zeigt [aria-pressed] (CSS), kein Inline-Ring mehr.
            // `topo-map-tool` bleibt als Identitäts-Hook erhalten. SVG-Sizing kommt aus `.topo2-btn--icon svg` (CSS).
            // cfg.size (Chef 14): optionale Größen-Variante ('small' | 'big' | …) → topo2-btn--<size> (überschreibt die
            // Größen-Tokens lokal). Ohne size = Standardgröße. Skaliert für weitere Größen ohne neue Boolean-Flags.
            // Text-Button (bar) wenn cfg.label gesetzt → feste Höhe, Breite nach Text, symmetrisches Padding;
            // sonst Icon-Button (--icon) mit SVG. (Chef-Charge 3: Overlay-Button als Text „Overlay".)
            const sizeCls = cfg.size ? ' topo2-btn--' + cfg.size : '';
            b.className = 'topo-map-tool topo2-btn ' + (cfg.label ? 'topo2-btn--bar' : 'topo2-btn--icon') + ' topo2-btn--outline' + sizeCls;
            b.dataset.key = cfg.key || '';
            b.title = cfg.title || '';
            if (cfg.title) { b.setAttribute('aria-label', cfg.title); }   // SR-Label aus dem (DE-)Titel
            if (cfg.label) { b.textContent = cfg.label; } else { b.innerHTML = cfg.svg || ''; }

            if (cfg.toggle) {
                this._setActive(b, !!cfg.active);
                b.addEventListener('click', () => {
                    const next = b.dataset.active !== '1';
                    this._setActive(b, next);
                    if (typeof cfg.onToggle === 'function') { cfg.onToggle(next); }
                });
            } else {
                b.addEventListener('click', () => { if (typeof cfg.onClick === 'function') { cfg.onClick(); } });
            }

            // cfg.pair (Chef 5): Items mit derselben pair-id teilen sich einen Slot (.topo2-btn-pair) statt je eine
            // Stack-Zeile; ohne pair landet der Button direkt im Stack.
            this._resolveParent(cfg.pair).appendChild(b);
            return b;
        }

        // Pair-Container je pair-id lazy anlegen (erstes Item legt an, weitere hängen sich dazu). Sitzt an der Stelle
        // im Stack, an der das erste gepaarte Item hinzugefügt wurde → gepaarte Items müssen aufeinanderfolgen.
        _resolveParent(pairId) {
            if (!pairId) { return this._container; }
            let p = this._pairs.get(pairId);
            if (!p) {
                p = document.createElement('div');
                p.className = 'topo2-btn-pair';
                p.dataset.pair = pairId;
                this._container.appendChild(p);
                this._pairs.set(pairId, p);
            }
            return p;
        }

        // Toggle-Zustand über [aria-pressed="true"] → CSS zeichnet den „eingerasteten" Akzent-Look (ersetzt den Inline-Rosa-Ring).
        // dataset.active bleibt die logische Quelle für die Click-Mechanik; aria-pressed wird gespiegelt.
        _setActive(b, active) {
            b.dataset.active = active ? '1' : '0';
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
        }

        // Aktiv-Zustand eines Toggle-Buttons von außen setzen (z.B. Sync mit echtem Status). Feuert KEIN onToggle → kein Loop.
        setActive(button, active) {
            if (button) { this._setActive(button, active); }
        }

        // Gegenstück zu attach(): Container aus den Karten-Controls nehmen (Rebuild bei Kontextwechsel im Reconciler).
        destroy() {
            if (this._pos === undefined) { return; }
            const arr = this._map.controls[this._pos];
            if (!arr) { return; }
            for (let i = arr.getLength() - 1; i >= 0; i--) {
                if (arr.getAt(i) === this._container) { arr.removeAt(i); }
            }
        }

        getContainer() { return this._container; }
    };
}
